import { sendRequest } from "./retrieve-data";
import { getClientId, internationalizeNumericValue } from "./utils";
import { getSelectedDateRange } from "./date-range";
import moment from "moment";
import { FUNNEL_ATTRIBUTES, NEGATIVE_PARAMETERS } from "@ts/constants";
import $ from "jquery";
import store from "../store";
import { updateActiveKey, updateChartsData } from "@features/chartsDataSlice";
import { setDashboardLoader } from "@features/dashboardLoaderSlice";
import { AGGREGATED_COLUMNS } from "@workers/row-aggregator.worker";
import * as Sentry from "@sentry/react";
import { TChartsData } from "./common-types";
import { showToast } from "./modals";
import { isNumber } from "underscore";
import {
    setCampaignLevelTotalSpend,
    setFunnelResponse,
} from "@/features/gridDataSlice";

export type TDate = {
    dateRange: string;
    startDate: moment.Moment;
    endDate: moment.Moment;
};

type TFunnelObj = Record<string, number | string>;

export type TFunnelResponse = {
    rows: Array<TFunnelObj>;
    status: boolean;
    comparedTotal: Array<TFunnelObj>;
    comparedTotalByProgramType: Array<TFunnelObj>;
    totalRows: Array<TFunnelObj>;
};

const colors = {
    negative: "#ff3b3b",
    positive: "#04ba13",
    primary: "#0d6efd",
    secondary: "#2d2e2d",
};

export async function getFunnelReport() {
    try {
        const date: TDate = getSelectedDateRange();
        const response = await sendRequest({
            data: {
                start: date.startDate.format("YYYY-MM-DD"),
                end: date.endDate.format("YYYY-MM-DD"),
            },
            method: "post",
            type: "reports/funnel",
            API_DOMAIN: process.env.VITE_API_DOMAIN || "/api",
            client: getClientId(),
        });

        if (!response.status) {
            throw new Error("Funnel Data: Internal Server Error");
        }

        store.dispatch(
            setCampaignLevelTotalSpend(
                response.comparedTotal.length
                    ? response.comparedTotal[0].spend
                    : 0
            )
        );

        // for tag type aggregate view
        store.dispatch(setFunnelResponse(response.comparedTotal[0]));

        store.dispatch(setDashboardLoader(false));

        setTimeout(() => {
            renderFunnelReport(date, response);
            sessionStorage.setItem("funnel-response", JSON.stringify(response));
        }, 250);
    } catch (err) {
        console.error(err);
        showToast("danger", (err as Error).message);
        Sentry.captureException(err);
    }
}

export function renderFunnelReport(date: TDate, response: TFunnelResponse) {
    const dateRange = date;
    const dt = moment(dateRange.endDate).diff(dateRange.startDate, "days") + 1;
    const compareStart = moment(dateRange.startDate)
        .subtract(dt, "days")
        .format("MMMM DD, YYYY");
    const compareEnd = moment(dateRange.endDate)
        .subtract(dt, "days")
        .format("MMMM DD, YYYY");

    $("#funnel-report-filter").html(
        `Compared to ${dt} days ago. (${compareStart}-${compareEnd})`
    );
    sessionStorage.setItem("funnel-data", JSON.stringify(response.totalRows));
    populateCards("performance-funnel", response.comparedTotal[0]);
}

function populateValue(
    element: Document,
    attribute: string,
    data: TFunnelObj,
    isRatio: boolean
) {
    const value =
        data && data[attribute] && isNumber(data[attribute])
            ? (data[attribute] as number) % 1 !== 0
                ? (data[attribute] as number).toFixed(1)
                : data[attribute]
            : 0;

    const processedValue = (
        AGGREGATED_COLUMNS.findIndex((attr) => attr === attribute) !== -1
            ? internationalizeNumericValue(Math.round(+value))
            : internationalizeNumericValue(value)
    ).toLocaleString();

    $(element)
        .find(`#${attribute}_value`)
        .text(
            `${processedValue}${
                ["ctr", "cr", "acos"].findIndex(
                    (attr) => attr === attribute
                ) !== -1
                    ? "%"
                    : ""
            }`
        );

    const isReversedColor =
        NEGATIVE_PARAMETERS.findIndex((p) => p === attribute) !== -1;

    // Resetting elements to their original state
    $(element)
        .find(`#${attribute}_delta_indicator,#${attribute}_delta_value`)
        .css({ display: "unset" });

    if (data && data[`${attribute}_change`]) {
        if ((data[`${attribute}_change`] as number) < 0) {
            $(element)
                .find(`#${attribute}_delta_indicator`)
                .css({
                    transform: `translate(${
                        isRatio ? "30" : "50"
                    }px, 30px) rotate(180deg)`,
                    fill: isReversedColor ? colors.positive : colors.negative,
                });
        } else {
            $(element)
                .find(`#${attribute}_delta_indicator`)
                .css({
                    transform: `translate(${
                        isRatio ? "40" : "50"
                    }px, 10px) rotate(120deg)`,
                    fill: isReversedColor ? colors.negative : colors.positive,
                });
        }
        $(element)
            .find(`#${attribute}_delta_value`)
            .text(`${(data[`${attribute}_change`] || 0).toLocaleString()}%`);
    } else {
        $(element)
            .find(`#${attribute}_delta_indicator,#${attribute}_delta_value`)
            .css({ display: "none" });
    }
}

function hoverEnterHandler(this: HTMLElement) {
    $(this)
        .find(".shape-primary")
        .css({
            fill: `${colors.primary}`,
            opacity: 0.6,
        })
        .siblings("text")
        .css({
            fill: "#fff",
        });

    $(this)
        .find(".shape-primary-inverse-stroke")
        .css({
            fill: `${colors.secondary}`,
            opacity: 0.6,
        })
        .siblings()
        .find("text")
        .css({
            fill: "#fff",
        });
}

function hoverLeaveHandler(this: HTMLElement) {
    $(this)
        .find(".shape-primary")
        .css({
            fill: colors.primary,
            opacity: 1,
        })
        .siblings("text")
        .css({
            fill: "#fff",
        });

    $(this)
        .find(".shape-primary-inverse-stroke")
        .css({
            fill: `#fff`,
        })
        .siblings()
        .find("text")
        .css({
            fill: `${colors.secondary}`,
        });
}

export function populateCards(
    elemId: string,
    data: TFunnelObj,
    triggerClick = true
) {
    const element = (
        document.getElementById(elemId) as HTMLIFrameElement | null
    )?.contentDocument;
    FUNNEL_ATTRIBUTES.forEach((attr) => {
        populateValue(element, attr.key, data, attr.isRatio);
    });

    if (!window.location.hash.startsWith("#modal")) {
        $(element)
            .find(".clickable,.clickable-inverse")
            .off("click", onCardClickHandler);
        $(element)
            .find(".clickable,.clickable-inverse")
            .css({ cursor: "pointer" })
            .on("click", onCardClickHandler)
            .on("pointerenter", hoverEnterHandler)
            .on("pointerleave", hoverLeaveHandler);
    }

    $(element).find(".rev-primary-color").css({ fill: colors.primary });

    $(element)
        .find(".rev-primary-color-stroke")
        .css({ stroke: colors.primary });
    if (triggerClick) {
        $(element).find("#spend_group").trigger("click");
    }
}

export function loadChart(attributes: Array<string>) {
    const chartsData: TChartsData[] = [];
    /* eslint-disable @typescript-eslint/no-unused-vars */
    //@ts-ignore

    for (const [index, attr] of attributes.entries()) {
        const seriesObject = {
            id: `${attr}Series`,
            name: FUNNEL_ATTRIBUTES.find((a) => a.key === attr).name,
            type: "areaspline",
            data: JSON.parse(sessionStorage.getItem("funnel-data"))?.map(
                (item: TFunnelObj) => {
                    return [
                        moment(
                            moment(item.date).format("YYYY-MM-DD"),
                            "YYYY-MM-DD"
                        ).unix() * 1000,
                        +(+item[attr]).toFixed(2),
                    ];
                }
            ),
            yAxis: attr,
        };
        chartsData.push(seriesObject);
    }

    store.dispatch(updateChartsData({ chartsData }));
}

export function updateChart(attribute: string, index: number | null = null) {
    const selectedCards =
        JSON.parse(sessionStorage.getItem("selectedCards")) || [];
    if (selectedCards.length === 2) {
        if (index !== null) {
            selectedCards[index] = attribute;
        } else {
            selectedCards.shift();
            selectedCards.push(attribute);
        }
    } else {
        selectedCards.push(attribute);
    }
    sessionStorage.setItem("selectedCards", JSON.stringify(selectedCards));
    store.dispatch(updateActiveKey(selectedCards));
    loadChart(selectedCards);
}

function onCardClickHandler(this: HTMLElement) {
    $(this).addClass("active");
    updateChart($(this).data("group"));
}
